import { createStore } from 'vuex';
import { THEME_LIGHT, chartTheme } from '@rdrive/theme';
import { normalizeLocale } from '@rdrive/i18n';

// THE APP STORE — DOMAIN state only (IA_ABSORPTION_PLAN Phase 1 step 6b-2b): the lookup objects
// built from DuckDB, the FAB's status/item-type filters, `showOpenItemsOnly`, the welcome/load
// flags, theme and build mode. The PLATFORM state and services — URL settings, the proxy
// transport, DuckDB, the data loader, the filter contexts, analytics, the boot — are the
// dashboard's (`createDashboard` / `useDashboard()` from @rdrive/plugin-dashboard-shell).
//
// A FACTORY taking that dashboard: `loadLookupObjectsFromDB` needs its DuckDB connection, and
// the compat delegates below read it. dashboard.config.js passes `createAppStore` as the config's
// `store`, and createDashboard calls it once with the dashboard it created.
//
// COMPAT DELEGATES (transitional). Pages and components did not change in 6b-2b, so every
// platform path they read through the store still resolves — to the DASHBOARD's object, never a
// copy: the state accessors and getters marked "delegate" below, and the settings mutations,
// which write the dashboard's own reactive `settings` (`state.settings` IS that object). New code
// reads the dashboard (useDashboard(), or useAppContext() in pages); the delegates go once the
// pages stop reading them.

const createStaticState = () => ({
  items: [], // Keep this for compatibility, but we'll primarily use DuckDB
  drawings: [],
  drawingsObj: {},
  categories: [],
  categoriesObj: {},
  itemTemplatesObj: {},
  statusTemplatesObj: {},
  formTemplatesObj: {},
  drawingLocationsObj: {},
  locationsObj: {},
  teamsObj: {},
  teamUsersObj: {},
  userTeamsObj: {},
  usersObj: {},
  permissionRulesObj: {},
});

/**
 * @param {import('@rdrive/plugin-dashboard-shell/src/dashboardContext.js').Dashboard} dashboard
 * @returns {import('vuex').Store}
 */
export function createAppStore(dashboard) {
  return createStore({
    state: {
      // --- compat delegates: the dashboard's objects, read through (no copies, no setters —
      // assigning one throws). `filterItemsContext`/`filterStatusHistoryContext` are the
      // dashboard's `filters.items`/`filters.statusHistory`; `settings` is its reactive settings,
      // so `state.settings.x = …` in a mutation below writes the platform object itself.
      get apiClient() { return dashboard.api; },
      get proxyFetcher() { return dashboard.fetcher; },
      get duckDBService() { return dashboard.duckdb; },
      get dataLoader() { return dashboard.loader; },
      get filterItemsContext() { return dashboard.filters.items; },
      get filterStatusHistoryContext() { return dashboard.filters.statusHistory; },
      get isDuckDBInitialized() { return dashboard.isDuckDBInitialized.value; },
      get settings() { return dashboard.settings; },

      // --- domain state
      showWelcomeDialog: true,
      loadDashboard: false,
      selectedItems: [],
      selectedTeam: 'Un-Assigned',
      statusFilters: {},
      itemTypeFilters: {},
      theme: THEME_LIGHT,
      buildMode: 'production',
      // Were `settings.*` until 6b-2b; app fields, not platform settings.
      showOpenItemsOnly: false,
      logoUrl: '',
      timeZone: 'UTC',
      static: createStaticState(),
    },
    mutations: {
      // --- compat delegates (platform writes go to the dashboard)
      setDuckDBInitialized(state, status) {
        dashboard.isDuckDBInitialized.value = status;
      },
      setSettings(state, newSettings) {
        // Merged INTO the dashboard's settings (until 6b-2b it replaced the store's own object).
        Object.assign(state.settings, newSettings);
      },
      updateSettingsField(state, { field, value }) {
        state.settings[field] = value;
      },
      setApiUrl(state, apiUrl) {
        state.settings.apiUrl = apiUrl;
      },
      setApiToken(state, token) {
        dashboard.setScopedToken(token);
      },
      setUserId(state, value) {
        state.settings.userId = value;
      },
      setProjectCode(state, value) {
        state.settings.projectCode = value;
      },
      setProjectName(state, value) {
        state.settings.projectName = value;
      },
      // The dashboard's setScopedToken is THE write path for a rotated token (its token refresher
      // calls it directly); this is the same write.
      setScopedToken(state, value) {
        dashboard.setScopedToken(value);
      },
      setValues(state, values) {
        // The dashboard's setValues: apiUrl, projectCode, projectName, scopedToken and the locale
        // (normalised — its ONE normalisation point). Nothing commits this since 6b-2b (the
        // dashboard's start() writes the URL values itself); kept as a delegate, theme included.
        dashboard.setValues(values);
        state.theme = values.theme;
      },
      setLocale(state, locale) {
        state.settings.locale = locale;
      },
      setFilterItemsContext(state, filterContext) {
        dashboard.filters.items = filterContext;
      },
      updateFilter(state, { chartId, ids }) {
        state.filterItemsContext.updateFilter(chartId, ids);
      },
      removeFilter(state, chartId) {
        state.filterItemsContext.removeFilter(chartId);
      },
      resetFilters(state) {
        state.filterItemsContext.resetFilters();
      },
      setFilterStatusHistoryContext(state, filterStatusHistoryContext) {
        dashboard.filters.statusHistory = filterStatusHistoryContext;
      },
      updateStatusHistoryFilter(state, { chartId, ids }) {
        state.filterStatusHistoryContext.updateFilter(chartId, ids);
      },
      removeStatusHistoryFilter(state, chartId) {
        state.filterStatusHistoryContext.removeFilter(chartId);
      },
      resetStatusHistoryFilters(state) {
        state.filterStatusHistoryContext.resetFilters();
      },

      // --- domain
      setTheme(state, newTheme) {
        state.theme = newTheme;
      },
      // No `setDataLoaded`: the data loader owns both halves it used to set — its own
      // `dataLoaded` ref (read by the getDataLoaded getter) and DuckDBService's query gate.
      setItems(state, items) {
        state.static.items = Object.freeze([...items]);
      },
      setCategories(state, categories) {
        state.static.categories = Object.freeze(categories);
      },
      setCategoriesObj(state, categoriesObj) {
        state.static.categoriesObj = Object.freeze(categoriesObj);
      },
      setItemTemplatesObj(state, itemTemplatesObj) {
        state.static.itemTemplatesObj = Object.freeze(itemTemplatesObj);
      },
      setStatusTemplatesObj(state, statusTemplatesObj) {
        state.static.statusTemplatesObj = Object.freeze(statusTemplatesObj);
      },
      setFormTemplatesObj(state, formTemplatesObj) {
        state.static.formTemplatesObj = Object.freeze(formTemplatesObj);
      },
      setDrawings(state, drawings) {
        state.static.drawings = Object.freeze(drawings);
      },
      setDrawingsObj(state, drawingsObj) {
        state.static.drawingsObj = Object.freeze(drawingsObj);
      },
      setDrawingLocationsObj(state, drawingLocationsObj) {
        state.static.drawingLocationsObj = Object.freeze(drawingLocationsObj);
      },
      setPermissionRulesObj(state, permissionRulesObj) {
        state.static.permissionRulesObj = Object.freeze(permissionRulesObj);
      },
      setLocationsObj(state, locationsObj) {
        state.static.locationsObj = Object.freeze(locationsObj);
      },
      setTeamsObj(state, teamsObj) {
        state.static.teamsObj = Object.freeze(teamsObj);
      },
      setTeamUsersObj(state, teamUsersObj) {
        state.static.teamUsersObj = Object.freeze(teamUsersObj);
      },
      setUserTeamsObj(state, userTeamsObj) {
        state.static.userTeamsObj = Object.freeze(userTeamsObj);
      },
      setUsersObj(state, usersObj) {
        state.static.usersObj = Object.freeze(usersObj);
      },
      setSelectedItems(state, selectedItems) {
        state.selectedItems = selectedItems;
      },
      setSelectedTeam(state, selectedTeam) {
        state.selectedTeam = selectedTeam;
      },
      setStatusFilters(state, filters) {
        state.statusFilters = { ...filters };
      },
      setItemTypeFilters(state, filters) {
        state.itemTypeFilters = { ...filters };
      },
      setLoadDashboard(state, loadDashboard) {
        state.loadDashboard = loadDashboard;
      },
      setShowWelcomeDialog(state, showWelcomeDialog) {
        state.showWelcomeDialog = showWelcomeDialog;
      },
      setBuildMode(state, buildMode) {
        state.buildMode = buildMode;
      },
      setLogoUrl(state, logoUrl) {
        state.logoUrl = logoUrl;
      },
      setTimeZone(state, timeZone) {
        state.timeZone = timeZone;
      },
      setShowOpenItemsOnly(state, mode) {
        state.showOpenItemsOnly = mode;
      },
    },
    actions: {
      // --- compat delegates
      async initializeDuckDB() {
        // The dashboard initialises DuckDB in its start(); this is the same call.
        return dashboard.initDuckDB();
      },
      // Replacement-instance path. Latent: the data loader's onLoaded hook (dashboard.config.js)
      // and ActivityPage initialise the EXISTING instances, so neither action is dispatched. Kept
      // so the set*Context mutations stay reachable: a fresh context from the dashboard's own
      // config entry, initialised, then installed as the dashboard's.
      initializeFilterContext({ commit, state }) {
        const filterContext = dashboard.newFilterContext('items');
        filterContext.initializeFilterContext({
          showOpenItemsOnly: state.showOpenItemsOnly,
        });
        commit('setFilterItemsContext', filterContext);
      },
      initializeStatusHistoryFilterContext({ commit }) {
        const filterStatusHistoryContext = dashboard.newFilterContext('statusHistory');
        filterStatusHistoryContext.initializeFilterContext();
        commit('setFilterStatusHistoryContext', filterStatusHistoryContext);
      },
      saveSettings({ commit }, settings) {
        commit('setSettings', settings);
      },
      updateSettingsField({ commit }, payload) {
        commit('updateSettingsField', payload);
      },
      updateApiUrl({ commit }, apiUrl) {
        commit('setApiUrl', apiUrl);
      },
      updateProjectCode({ commit }, projectCode) {
        commit('setProjectCode', projectCode);
      },
      updateApiProjectName({ commit }, projectName) {
        commit('setProjectName', projectName);
      },
      updateUserId({ commit }, userId) {
        commit('setUserId', userId);
      },
      updateValues({ commit }, values) {
        commit('setValues', values);
      },
      updateLocale({ commit }, locale) {
        commit('setLocale', normalizeLocale(locale));
      },

      // --- domain
      // The lookup objects pages read from the store, built from the loaded tables. Run by the
      // data loader's onLoaded hook (dashboard.config.js), before `dataLoaded` flips.
      async loadLookupObjectsFromDB({ commit }) {
        // The company logo for the report header (`useItemsGridReport`'s `ProjectInfo.logoURL`;
        // falsy → the report service's default) — issue-analysis's query. Restored 2026-10-28: it
        // went in 9a8ce81 (2025-02), since when nothing set `logoUrl` and every report carried the
        // default logo.
        try {
          const companyResult = await dashboard.conn.query('SELECT logoUrl FROM company LIMIT 1;');
          const logoUrl = companyResult.toArray()[0]?.toJSON().logoUrl;
          if (logoUrl) {
            commit('setLogoUrl', logoUrl);
          } else {
            console.warn('No data found in company table for logoUrl');
          }
        } catch (error) {
          console.error('Error querying company table for logoUrl:', error);
        }

        try {
          const teamsResult = await dashboard.conn.query('SELECT * FROM teams');
          const teamsObj = {};
          teamsResult.toArray().forEach(row => {
            const team = row.toJSON();
            const id = team.Id || team.id;
            if (id) {
              teamsObj[id] = {
                id: id,
                name: team.Name || team.name || '',
                teamProfileTitle: team.TeamProfileTitle || team.teamProfileTitle || ''
              };
            }
          });
          commit('setTeamsObj', teamsObj);
        } catch (error) {
          console.error('Error loading teams lookup from DB:', error);
        }

        try {
          // Schema: Key (team ID) | Value (JSON array of users)
          const usersResult = await dashboard.conn.query('SELECT Key, Value FROM users');
          const usersObj = {};
          usersResult.toArray().forEach(row => {
            const rowData = row.toJSON();
            let teamUsers = rowData.Value || rowData.value;

            if (typeof teamUsers === 'string') {
              try {
                teamUsers = JSON.parse(teamUsers);
              } catch {
                teamUsers = [];
              }
            }

            if (Array.isArray(teamUsers)) {
              teamUsers.forEach(u => {
                const id = u.Id || u.id;
                if (id && !usersObj[id]) {
                  usersObj[id] = {
                    displayName: u.DisplayName || u.displayName || '',
                    avatarUrl: u.AvatarUrl || u.avatarUrl || ''
                  };
                }
              });
            }
          });
          commit('setUsersObj', usersObj);
        } catch (error) {
          console.error('Error loading users lookup from DB:', error);
        }
      },
      updateItems({ commit }, updatedItems) {
        commit('setItems', updatedItems);
      },
      updateTheme({ commit }, theme) {
        commit('setTheme', theme);
      },
      updateLoadDashboard({ commit }, loadDashboard) {
        commit('setLoadDashboard', loadDashboard);
      },
      updateShowWelcomeDialog({ commit }, showWelcomeDialog) {
        commit('setShowWelcomeDialog', showWelcomeDialog);
      },
      updateBuildMode({ commit }, buildMode) {
        commit('setBuildMode', buildMode);
      },
      updateLogoUrl({ commit }, logoUrl) {
        commit('setLogoUrl', logoUrl);
      },
      updateTimeZone({ commit }, timeZone) {
        commit('setTimeZone', timeZone);
      },
      updateShowOpenItemsOnly({ commit }, mode) {
        commit('setShowOpenItemsOnly', mode);
      },
      // Deleted before 6b-2b: `initializeFilterItemsContext` (committed a mutation that does not
      // exist) and the `updateFilter`/`removeFilter`/`resetFilters` ACTIONS, which shadowed the
      // same-named mutations; none was ever dispatched — components commit the mutations.
    },
    getters: {
      // --- compat delegates (the dashboard's objects and values)
      getDuckDBService: (state) => state.duckDBService,
      getDataLoader: (state) => state.dataLoader,
      isDuckDBInitialized: (state) => state.isDuckDBInitialized,
      // The loader's ref, so the flag every page reads cannot drift from the load itself
      getDataLoaded: (state) => state.dataLoader.dataLoaded.value,
      getDBConnection: (state) => state.duckDBService.conn,
      getSettings: (state) => state.settings,
      getApiClient: (state) => state.apiClient,
      getApiUrl: (state) => state.settings.apiUrl,
      getProjectCode: (state) => state.settings.projectCode,
      getProjectName: (state) => state.settings.projectName,
      getScopedToken: (state) => state.settings.scopedToken,
      getUrl: (state) => state.settings.apiUrl,
      getSelectedProject: (state) => state.settings.projectCode,
      getUserId: (state) => state.settings.userId,
      currentLocale: (state) => state.settings.locale,
      executeFilteredQuery: (state) => async (selectQuery) => {
        if (state.filterItemsContext) {
          return await state.filterItemsContext.executeFilteredQuery(selectQuery);
        }
        return null;
      },

      // --- domain
      getLoadDashboard: (state) => state.loadDashboard,
      getShowWelcomeDialog: (state) => state.showWelcomeDialog,
      getBuildMode: (state) => state.buildMode,
      getShowOpenItemsOnly: (state) => state.showOpenItemsOnly,
      getLogoUrl: (state) => state.logoUrl,
      getItems: (state) => state.static.items,
      getCategories: (state) => state.static.categories,
      getCategoriesObj: (state) => state.static.categoriesObj,
      getItemTemplatesObj: (state) => state.static.itemTemplatesObj,
      getStatusTemplatesObj: (state) => state.static.statusTemplatesObj,
      getFormTemplatesObj: (state) => state.static.formTemplatesObj,
      getDrawings: (state) => state.static.drawings,
      getDrawingsObj: (state) => state.static.drawingsObj,
      getDrawingLocationsObj: (state) => state.static.drawingLocationsObj,
      getLocationsObj: (state) => state.static.locationsObj,
      getTeamsObj: (state) => state.static.teamsObj,
      getTeamUsersObj: (state) => state.static.teamUsersObj,
      getUserTeamsObj: (state) => state.static.userTeamsObj,
      getUsersObj: (state) => state.static.usersObj,
      getPermissionRulesObj: (state) => state.static.permissionRulesObj,
      getSelectedItems: (state) => state.selectedItems,
      getSelectedTeam: (state) => state.selectedTeam,
      getStatusFilters: (state) => state.statusFilters,
      getItemTypeFilters: (state) => state.itemTypeFilters,
      // `state.theme` is 'light' | 'dark' (App/setup.js commits nothing else); chartTheme()
      // falls back to light for anything that is not 'dark'.
      chartFontColor: (state) => chartTheme(state.theme).fontColor,
      getFilteredItems: () => {
        return [];
      },
    },
  });
}
